import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ChallengeApiRecord,
  CreateChallengeRequest,
  CreateChallengeResponse,
} from "../../types/gamePass";
import type {
  ChallengeDraft,
} from "../../components/GamePass/CreateSeason/createSeasonData";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntityOrNull } from "./responseMappers";

/** Variables accepted by the challenge create mutation. */
export type CreateChallengeVariables = {
  seasonId: string;
  sortOrder: number;
  draft: ChallengeDraft;
  /** S3 object key of the uploaded challenge icon, resolved by the caller via
   *  `uploadGamePassImage` (presign + PUT) before the mutation fires. Only
   *  sent on the wire when present so a challenge without an icon never ships
   *  an empty key. */
  imgKey?: string | null;
};

/**
 * Convert the UI's local challenge draft into the BE
 * `POST /game-pass-admin/v2/admin/challenges` body.
 *
 * Notes:
 *   - `sort_order` is zero-based in the BE contract; the card's `index` prop
 *     is one-based for UI labels, so the caller converts it before passing
 *     the value here.
 *   - `data_source` is still a temporary constant because the current Create
 *     Challenge UI does not expose a dedicated selector yet.
 *   - `path_availability` follows the current Step 2 tab (`FREE`/`PREMIUM`).
 *   - Rewards are persisted separately after create via
 *     `PUT /game-pass-admin/v2/admin/challenges/{challenge_id}/reward` — see
 *     `useUpdateChallengeReward`. */
export const mapChallengeDraftToCreateChallengeRequest = ({
  seasonId,
  sortOrder,
  draft,
  imgKey,
}: CreateChallengeVariables): CreateChallengeRequest => {
  const trimmedDescription = draft.description.trim();
  return {
    season_id: seasonId,
    name: draft.name.trim(),
    sort_order: sortOrder,
    ...(trimmedDescription ? { description: trimmedDescription } : {}),
    ...(imgKey ? { img_key: imgKey } : {}),
    data_source: "SPORTSBOOK",
    path_availability: draft.pathAvailability,
  };
};

const postCreateChallenge = async (
  variables: CreateChallengeVariables,
): Promise<ChallengeApiRecord | null> => {
  const response =
    await getPrivateInstance().post<CreateChallengeResponse>(
      API_ROUTES.CREATE_CHALLENGE,
      mapChallengeDraftToCreateChallengeRequest(variables),
    );
  // Tolerant unwrap (FE-1692): the POST already succeeded, so an
  // unrecognised / missing body must not be surfaced as a creation failure.
  // Returns `null` when no challenge payload is present; the caller commits
  // the challenge as saved (without a persisted id) instead of erroring.
  return unwrapGamePassEntityOrNull(response.data, "challenge");
};

/** React Query mutation for `POST /game-pass-admin/v2/admin/challenges`. */
export const useCreateChallenge = (): UseMutationResult<
  ChallengeApiRecord | null,
  Error,
  CreateChallengeVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "challenges", "create"],
    mutationFn: postCreateChallenge,
    onSuccess: (_challenge, variables) => {
      // Refetch the season's challenge listing so the Season Details
      // editor shows the new challenge in place — without this the
      // in-place view stays stale until a remount (navigate away + back).
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "challenges", "list", variables.seasonId],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "detail", variables.seasonId],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "seasons", "list"],
      });
    },
  });
};
